import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import Layout from "./components/Layout";
import ScrollToTop from "./components/ScrollToTop";
import Home from "./pages/main/Home";
import About from "./pages/about/About";
import Gallery from "./pages/gallery/Gallery";
import Dashboard from "./pages/dashboard/Dashboard";
import Community from "./pages/community/Community";
import CreatePost from "./pages/community/CreatePost";
import Members from "./pages/members/Members";
import MemberDetail from "./pages/members/MemberDetail";
import EditProfile from "./pages/members/EditProfile";
import Login from "./pages/auth/Login";
import Register from "./pages/auth/Register";
import VerifyEmail from "./pages/auth/VerifyEmail";
import PostOpportunity from "./pages/create/PostOpportunity";
import PrivacyPolicy from "./pages/static/PrivacyPolicy";
import TermsOfService from "./pages/static/TermsOfService";
import Questions from "./pages/questions/Questions";
import AskQuestion from "./pages/questions/AskQuestion";
import QuestionDetail from "./pages/questions/QuestionDetail";
import Volunteer from "./pages/volunteer/Volunteer";
import VolunteerDetail from "./pages/volunteer/VolunteerDetail";
import ApplyVolunteer from "./pages/volunteer/ApplyVolunteer";
import ApplicationForm from "./pages/scholarship/ApplicationForm";
import PublicApplicationForm from "./pages/scholarship/PublicApplicationForm";
import ApplicationStatus from "./pages/scholarship/ApplicationStatus";
import ApplicationDetail from "./pages/scholarship/ApplicationDetail";
import ApplicationSuccess from "./pages/scholarship/ApplicationSuccess";
import ApplicationInstructions from "./pages/scholarship/ApplicationInstructions";
import JobBoard from "./pages/jobs/JobBoard";
import JobDetail from "./pages/jobs/JobDetail";
import Announcements from "./pages/announcements/Announcements";
import ApplicantDashboard from "./pages/dashboard/ApplicantDashboard";
import StudentManagerDashboard from "./pages/dashboard/StudentManagerDashboard";
import EmployerDashboard from "./pages/dashboard/EmployerDashboard";
import AdminDashboard from "./pages/dashboard/AdminDashboard";
import ManageUsers from "./pages/admin/ManageUsers";
import ManageSessions from "./pages/admin/ManageSessions";
import CreateSession from "./pages/admin/CreateSession";
import ManageEmployers from "./pages/admin/ManageEmployers";
import ReviewEmployer from "./pages/admin/ReviewEmployer";
import ManageAnnouncements from "./pages/admin/ManageAnnouncements";
import SystemSettings from "./pages/admin/SystemSettings";
import ViewReports from "./pages/admin/ViewReports";
import SessionDetail from "./pages/admin/SessionDetail";
import PostJob from "./pages/jobs/PostJob";
import ManageJobApplications from "./pages/jobs/ManageJobApplications";
import PostAnnouncement from "./pages/announcements/PostAnnouncement";
import ReviewAlumni from "./pages/manager/ReviewAlumni";
import ScheduleInterview from "./pages/interviews/ScheduleInterview";
import ApplyAlumni from "./pages/alumni/ApplyAlumni";
import EditEmployerProfile from "./pages/employer/EditEmployerProfile";
import EmployerKYC from "./pages/employer/EmployerKYC";
// About subpages
import OurStory from "./pages/about/OurStory";
import OurApproach from "./pages/about/OurApproach";
import OurTeam from "./pages/about/OurTeam";
import OurSupporters from "./pages/about/OurSupporters";
import OurPartners from "./pages/about/OurPartners";
// Programs subpages
import EmployabilitySkills from "./pages/programs/EmployabilitySkills";
import MentorshipProgram from "./pages/programs/MentorshipProgram";
// Get Involved pages
import BeADonor from "./pages/get-involved/BeADonor";
import BeACorporateDonor from "./pages/get-involved/BeACorporateDonor";
// Resources pages
import NewsAndEvents from "./pages/resources/NewsAndEvents";
import EventDetail from "./pages/resources/EventDetail";
import Newsletters from "./pages/resources/Newsletters";
import Careers from "./pages/resources/Careers";
import Contact from "./pages/static/Contact";
import SuccessStories from "./pages/success-stories/SuccessStories";
import Settings from "./pages/settings/Settings";

function App() {
  return (
    <AuthProvider>
      <Router>
        <ScrollToTop />
        <AppRoutes />
      </Router>
    </AuthProvider>
  );
}

// Create a ProtectedRoute component (must be inside AuthProvider)
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  // Show loading while checking auth
  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  // Redirect to login if no user
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

// Routes component (inside AuthProvider context)
function AppRoutes() {
  return (
    <Layout>
      <Routes>
        {/* Public Routes */}
        <Route path="/" element={<Home />} />
        <Route path="/about" element={<About />} />
        <Route path="/about/story" element={<OurStory />} />
        <Route path="/about/approach" element={<OurApproach />} />
        <Route path="/about/team" element={<OurTeam />} />
        <Route path="/about/supporters" element={<OurSupporters />} />
        <Route path="/about/partners" element={<OurPartners />} />
        <Route path="/programs/employability-skills" element={<EmployabilitySkills />} />
        <Route path="/programs/mentorship" element={<MentorshipProgram />} />
        <Route path="/get-involved/donor" element={<BeADonor />} />
        <Route path="/get-involved/corporate-donor" element={<BeACorporateDonor />} />
        <Route path="/resources/news-events" element={<NewsAndEvents />} />
        <Route path="/resources/news-events/:id" element={<EventDetail />} />
        <Route path="/resources/newsletters" element={<Newsletters />} />
        <Route path="/resources/careers" element={<Careers />} />
        <Route path="/gallery" element={<Gallery />} />
        <Route path="/success-stories" element={<SuccessStories />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<TermsOfService />} />

        {/* Protected Routes - Require Login */}
        <Route path="/dashboard" element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        } />
        <Route path="/community" element={
          <ProtectedRoute>
            <Community />
          </ProtectedRoute>
        } />
        <Route path="/community/create" element={
          <ProtectedRoute>
            <CreatePost />
          </ProtectedRoute>
        } />
        <Route path="/members" element={
          <ProtectedRoute>
            <Members />
          </ProtectedRoute>
        } />
        <Route path="/members/:id" element={
          <ProtectedRoute>
            <MemberDetail />
          </ProtectedRoute>
        } />
        <Route path="/profile/edit" element={
          <ProtectedRoute>
            <EditProfile />
          </ProtectedRoute>
        } />
        <Route path="/settings" element={
          <ProtectedRoute>
            <Settings />
          </ProtectedRoute>
        } />
        <Route path="/volunteer" element={
          <ProtectedRoute>
            <Volunteer />
          </ProtectedRoute>
        } />
        <Route path="/post_opportunity" element={
          <ProtectedRoute>
            <PostOpportunity />
          </ProtectedRoute>
        } />
        <Route path="/questions" element={
          <ProtectedRoute>
            <Questions />
          </ProtectedRoute>
        } />
        <Route path="/questions/ask" element={
          <ProtectedRoute>
            <AskQuestion />
          </ProtectedRoute>
        } />
        <Route path="/questions/:id" element={
          <ProtectedRoute>
            <QuestionDetail />
          </ProtectedRoute>
        } />
        <Route path="/volunteer/:id" element={
          <ProtectedRoute>
            <VolunteerDetail />
          </ProtectedRoute>
        } />
        <Route path="/volunteer/:id/apply" element={
          <ProtectedRoute>
            <ApplyVolunteer />
          </ProtectedRoute>
        } />

        {/* Scholarship Platform Routes */}
        <Route path="/application-instructions" element={<ApplicationInstructions />} />
        <Route path="/apply-public" element={<PublicApplicationForm />} />
        <Route path="/application-success" element={<ApplicationSuccess />} />
        <Route path="/apply" element={
          <ProtectedRoute>
            <ApplicationForm />
          </ProtectedRoute>
        } />
        <Route path="/applications" element={
          <ProtectedRoute>
            <ApplicationStatus />
          </ProtectedRoute>
        } />
        <Route path="/applications/:id" element={
          <ProtectedRoute>
            <ApplicationDetail />
          </ProtectedRoute>
        } />
        <Route path="/applications/:id/review" element={
          <ProtectedRoute>
            <ApplicationDetail />
          </ProtectedRoute>
        } />
        <Route path="/jobs" element={
          <ProtectedRoute>
            <JobBoard />
          </ProtectedRoute>
        } />
        <Route path="/jobs/:id" element={
          <ProtectedRoute>
            <JobDetail />
          </ProtectedRoute>
        } />
        <Route path="/jobs/post" element={
          <ProtectedRoute>
            <PostJob />
          </ProtectedRoute>
        } />
        <Route path="/jobs/:id/manage" element={
          <ProtectedRoute>
            <ManageJobApplications />
          </ProtectedRoute>
        } />
        <Route path="/announcements" element={
          <ProtectedRoute>
            <Announcements />
          </ProtectedRoute>
        } />
        <Route path="/post_announcement" element={
          <ProtectedRoute>
            <PostAnnouncement />
          </ProtectedRoute>
        } />

        {/* Role-based Dashboards */}
        <Route path="/dashboard/applicant" element={
          <ProtectedRoute>
            <ApplicantDashboard />
          </ProtectedRoute>
        } />
        <Route path="/dashboard/student-manager" element={
          <ProtectedRoute>
            <StudentManagerDashboard />
          </ProtectedRoute>
        } />
        <Route path="/dashboard/employer" element={
          <ProtectedRoute>
            <EmployerDashboard />
          </ProtectedRoute>
        } />
        <Route path="/dashboard/admin" element={
          <ProtectedRoute>
            <AdminDashboard />
          </ProtectedRoute>
        } />

        {/* Admin Management Routes */}
        <Route path="/admin/users" element={
          <ProtectedRoute>
            <ManageUsers />
          </ProtectedRoute>
        } />
        <Route path="/admin/sessions" element={
          <ProtectedRoute>
            <ManageSessions />
          </ProtectedRoute>
        } />
        <Route path="/admin/sessions/create" element={
          <ProtectedRoute>
            <CreateSession />
          </ProtectedRoute>
        } />
        <Route path="/admin/sessions/:id" element={
          <ProtectedRoute>
            <SessionDetail />
          </ProtectedRoute>
        } />
        <Route path="/admin/employers" element={
          <ProtectedRoute>
            <ManageEmployers />
          </ProtectedRoute>
        } />
        <Route path="/admin/employers/:id/review" element={
          <ProtectedRoute>
            <ReviewEmployer />
          </ProtectedRoute>
        } />
        <Route path="/admin/announcements" element={
          <ProtectedRoute>
            <ManageAnnouncements />
          </ProtectedRoute>
        } />
        <Route path="/admin/settings" element={
          <ProtectedRoute>
            <SystemSettings />
          </ProtectedRoute>
        } />
        <Route path="/admin/reports" element={
          <ProtectedRoute>
            <ViewReports />
          </ProtectedRoute>
        } />

        {/* Student Manager Routes */}
        <Route path="/alumni/review" element={
          <ProtectedRoute>
            <ReviewAlumni />
          </ProtectedRoute>
        } />
        <Route path="/interviews/schedule" element={
          <ProtectedRoute>
            <ScheduleInterview />
          </ProtectedRoute>
        } />

        {/* Alumni Routes */}
        <Route path="/alumni/apply" element={
          <ProtectedRoute>
            <ApplyAlumni />
          </ProtectedRoute>
        } />

        {/* Employer Routes */}
        <Route path="/employer/kyc" element={
          <ProtectedRoute>
            <EmployerKYC />
          </ProtectedRoute>
        } />
        <Route path="/employer/profile" element={
          <ProtectedRoute>
            <EditEmployerProfile />
          </ProtectedRoute>
        } />
      </Routes>
    </Layout>
  );
}

export default App;